import { Dropdown } from 'react-bootstrap';
import {
  buildCloudwatchLogsUrl,
  buildEcsTaskUrl,
  buildStepFunctionsUrl,
  type DeploymentRecord,
} from '@/services/deploymentService';
import { getConfigValue } from '@/services/configService';
import { resolveDeploymentLogTarget } from '@/services/logsService';
import { isEcsLinkAlive, isFailed, isGroupSummary, isLive } from '@/utils/deployStatus';
import './deployments.css';

export interface RunMenuHandlers {
  onRunAgain?: (record: DeploymentRecord) => void;
  onStop?: (record: DeploymentRecord) => void;
  onMarkFailed?: (record: DeploymentRecord) => void;
  onMarkResolved?: (record: DeploymentRecord) => void;
  /** Block deploys to the run's client, or allow them again. The page opens the dialog that asks. */
  onBlockDeploys?: (record: DeploymentRecord) => void;
}

interface RunMoreMenuProps extends RunMenuHandlers {
  record: DeploymentRecord;
  /** Why "Run again" is off for this run, when it is. */
  runAgainBlocked?: string;
  /** The run's client is marked do not deploy now. The menu then offers to allow deploys again. */
  deployBlocked?: boolean;
  /** Leave Stop out of the menu when the view already shows it as a button. */
  hideStop?: boolean;
  /** A full-size button, for a page header. Row menus are small. */
  large?: boolean;
}

/**
 * Everything you can do with a run beyond opening its log, in one menu: the
 * three AWS console links, copy the id, run it again, and the state changes.
 * The ones that cannot be undone come last, in red.
 */
export function RunMoreMenu({
  record,
  onRunAgain,
  onStop,
  onMarkFailed,
  onMarkResolved,
  onBlockDeploys,
  runAgainBlocked,
  deployBlocked = false,
  hideStop = false,
  large = false,
}: RunMoreMenuProps) {
  const region = getConfigValue('AWS_REGION') || 'us-east-1';
  const group = isGroupSummary(record);
  const live = isLive(record);
  const target = resolveDeploymentLogTarget(record);
  const cloudwatchUrl = group ? null : buildCloudwatchLogsUrl(region, target.group, target.stream);
  const stepFunctionsUrl = buildStepFunctionsUrl(region, record.sfnExecutionArn);
  // ECS forgets a stopped task after about an hour. A link to it then opens an empty page, so it is left out.
  const ecsUrl = !group && isEcsLinkAlive(record) ? buildEcsTaskUrl(region, record.ecsTaskArn) : null;

  const copyId = () => {
    void navigator.clipboard.writeText(record.deploymentId).catch((e) => console.error('Copy failed', e));
  };

  const canRunAgain = Boolean(onRunAgain) && !group && !live && Boolean(record.imageTag);
  const canResolve = Boolean(onMarkResolved) && !group && isFailed(record);
  const canStop = Boolean(onStop) && live && !hideStop;
  const canMarkFailed = Boolean(onMarkFailed) && !group && live;
  // A group's summary row names no client, and neither does a row with its client missing.
  const canBlock = Boolean(onBlockDeploys) && !group && Boolean(record.clientName);

  return (
    <Dropdown align="end" className="deploy-more">
      <Dropdown.Toggle
        variant="outline-secondary"
        size={large ? undefined : 'sm'}
        id={`deploy-more-${record.deploymentId}`}
      >
        More
      </Dropdown.Toggle>
      {/* Fixed positioning lets the menu leave the table's scroll box, which would cut it off. */}
      <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
        {cloudwatchUrl && (
          <Dropdown.Item href={cloudwatchUrl} target="_blank" rel="noopener noreferrer">
            Open in CloudWatch
          </Dropdown.Item>
        )}
        {stepFunctionsUrl && (
          <Dropdown.Item href={stepFunctionsUrl} target="_blank" rel="noopener noreferrer">
            Open in Step Functions
          </Dropdown.Item>
        )}
        {ecsUrl && (
          <Dropdown.Item href={ecsUrl} target="_blank" rel="noopener noreferrer">
            Open in ECS
          </Dropdown.Item>
        )}
        <Dropdown.Item as="button" onClick={copyId}>
          {group ? 'Copy group run ID' : 'Copy deployment ID'}
        </Dropdown.Item>
        {canRunAgain && (
          <Dropdown.Item
            as="button"
            onClick={() => onRunAgain?.(record)}
            disabled={Boolean(runAgainBlocked)}
            title={runAgainBlocked}
          >
            Run again
          </Dropdown.Item>
        )}
        {canBlock && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item as="button" onClick={() => onBlockDeploys?.(record)}>
              {deployBlocked ? 'Allow deploys to this client again' : 'Block deploys to this client'}
            </Dropdown.Item>
          </>
        )}
        {(canResolve || canStop || canMarkFailed) && <Dropdown.Divider />}
        {canResolve && (
          <Dropdown.Item as="button" onClick={() => onMarkResolved?.(record)}>
            Mark resolved
          </Dropdown.Item>
        )}
        {canStop && (
          <Dropdown.Item as="button" className="deploy-more__danger" onClick={() => onStop?.(record)}>
            {group ? 'Stop group run' : 'Stop deploy'}
          </Dropdown.Item>
        )}
        {canMarkFailed && (
          <Dropdown.Item as="button" className="deploy-more__danger" onClick={() => onMarkFailed?.(record)}>
            Mark failed
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default RunMoreMenu;
